﻿import komapping from 'knockout.mapping';
import ko from 'knockout';
import { format, formatDistanceStrict as formatDistanceToNow } from 'date-fns';
import { da } from 'date-fns/locale';

import { MeetingStatus } from './enums';
import { MeetingDto } from './interfaces';
import { Log } from './log';
import { R, format as formatString } from './waiting-list.resx';

class MeetingViewModel {
    public videoRoomKey = ko.observable<string>();
    public lastActivity = ko.observable<Date>();
    public firstCurrentUserJoinedOn = ko.observable<Date>();
    public createdOn = ko.observable<Date>();
    public url = ko.observable<string>();

    private _participants = ko.observableArray<string>();
    private _startTime = ko.observable<Date>();
    private _status = ko.observable<MeetingStatus>();

    public participants = ko.pureComputed(() =>
        this._participants().join(', '),
    );

    public startTime = ko.pureComputed(() =>
        format(this._startTime(), 'HH:mm'),
    );

    public status = ko.pureComputed(() => {
        if (this._status() == MeetingStatus.InProgress) return R.Status_Ongoing;
        return formatString(
            R.Status_ArrivedInPast,
            formatDistanceToNow(this.firstCurrentUserJoinedOn(), new Date(), {
                locale: da,
            }),
        );
    });

    public isWaiting = ko.pureComputed(
        () => this._status() == MeetingStatus.Waiting,
    );

    constructor(meetingDto: MeetingDto) {
        this.createdOn(new Date());
        this.fromDto(meetingDto);
    }

    public fromDto(meetingDto: MeetingDto): void {
        this.videoRoomKey(meetingDto.videoRoomKey);
        this.url(meetingDto.videoRoomLink);
        this._startTime(new Date(meetingDto.startTime));
        this._status(meetingDto.status);
        this._participants(
            meetingDto.videoSessions.map(
                (vs) => vs.displayName || R.AnonymousDisplayName,
            ),
        );
        this.lastActivity(
            meetingDto.lastActivity ? new Date(meetingDto.lastActivity) : null,
        );
        this.firstCurrentUserJoinedOn(
            meetingDto.firstCurrentUserJoinedOn
                ? new Date(meetingDto.firstCurrentUserJoinedOn)
                : null,
        );
    }
}

class WaitingListViewModel {
    public calls = komapping.fromJS<MeetingViewModel>([]);

    public numberOfActiveMeetings = ko.pureComputed(
        () =>
            this.calls().filter((c: MeetingViewModel) => !c.isWaiting()).length,
    );

    public isEmpty = ko.pureComputed(() => this.calls().length == 0);

    public isReady = ko.observable(false);

    private _departmentId: number;

    private _errorCount = 0;

    private readonly sound = document.getElementById(
        'ding',
    ) as HTMLAudioElement;

    constructor(departmentId: number) {
        this._departmentId = departmentId;

        this.getMeetings();

        setInterval(() => {
            this.getMeetings();
        }, 4000);

        this.calls.subscribe(
            (changes: Array<{ status: string }>) => {
                let hasNewItems = false;
                changes.forEach((change: { status: string }) => {
                    if (change.status === 'added') {
                        hasNewItems = true;
                    }
                });
                if (hasNewItems) {
                    this.playSound();
                }
            },
            null,
            'arrayChange',
        );
    }

    private playSound(): void {
        const promise = this.sound.play();
        if (promise) {
            promise.catch((error) => {
                Log.info('Could not play sound', error);
            });
        }
    }

    private getMeetings(): void {
        fetch('/api/meetings/active?departmentId=' + this._departmentId)
            .then((response) => {
                if (response.status == 401) {
                    Log.error(
                        'Unauthorized. Redirecting to login page. ' +
                            response.status +
                            ': ' +
                            response.statusText,
                    );
                    document.location.href = '/login';
                }
                if (!response.ok) {
                    throw Error(response.status + ': ' + response.statusText);
                }
                return response.json();
            })
            .then((json) => {
                const o = { calls: json };
                komapping.fromJS(
                    o,
                    {
                        calls: {
                            key: (data: {
                                videoRoomKey: KnockoutObservable<string>;
                            }): string => {
                                return ko.utils.unwrapObservable(
                                    data.videoRoomKey,
                                );
                            },
                            create: (options: {
                                data: MeetingDto;
                            }): MeetingViewModel => {
                                return new MeetingViewModel(options.data);
                            },
                            update: (options: {
                                data: MeetingDto;
                                target: MeetingViewModel;
                            }): MeetingViewModel => {
                                options.target.fromDto(options.data);
                                return options.target;
                            },
                        },
                    },
                    this,
                );
                this.isReady(true);
            })
            .catch((ex) => {
                this._errorCount++;
                // throttle error logging
                if (this._errorCount % 5 == 0) {
                    Log.error(
                        'GET /api/meetings/active failed ' +
                            this._errorCount +
                            ' times',
                        ex,
                    );
                } else {
                    Log.info('GET /api/meetings/active failed', ex);
                }
            });
    }
}

export function WaitingList(departmentId: number): void {
    ko.applyBindings(
        new WaitingListViewModel(departmentId),
        document.getElementById('waiting-room'),
    );
}
